import { setErrorMess } from "@/lib/features/statusSlice";
import { useAppDispatch } from "@/lib/hooks";
import {
  paramsPurchasedReports,
  paramsReport,
  reportFeaturedParams,
} from "@/models/Params";
import { TYPE_FAVORITE } from "@/models/report";
import ReportServices from "@/services/main/reportServices";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useDispatch } from "react-redux";

interface ReportALLProps {
  queryKey?: string;
  take?: number;
  cursor?: number | string;
  categoryIds?: string;
  keyword?: string | null;
  sortedField?: string;
  isDescending?: boolean;
  isComingSoon?: boolean;
  onlyFavorite?: boolean;
  userId?: number;
  onSettled?: (response: any) => void;
  isLoggedIn?: boolean;
  types?: number[];
  isRecentlyPurchase?: boolean;
  typeFavorite?: number[];
  lang?: string;
  initialData?: any;
  isPurchased?: boolean;
  queryClient?: any;
}

export const useReportALL = (props: ReportALLProps) => {
  const {
    queryKey,
    take,
    isDescending,
    isComingSoon,
    onlyFavorite,
    userId,
    sortedField,
    categoryIds,
    keyword,
    lang,
    typeFavorite,
    initialData,
    isPurchased,
  } = props;

  return useInfiniteQuery({
    queryKey: [queryKey, isDescending, keyword, lang, categoryIds],
    queryFn: async ({ pageParam }: { pageParam?: number | string }) => {
      const params: paramsReport = {
        sortedField: sortedField,
        isDescending: isDescending,
        take: take,
        cursor: pageParam,
        isComingSoon: isComingSoon,
        onlyFavorite: onlyFavorite,
        userId: userId,
        categoryIds: categoryIds,
        keyword: keyword,
        typeFavorite: typeFavorite,
        lang: lang,
        isPurchased: isPurchased,
      };
      const result = await ReportServices.getAllReportHomePage(params);
      return result;
    },
    placeholderData:
      categoryIds || onlyFavorite
        ? undefined
        : initialData
          ? {
              pages: [initialData],
              pageParams: [undefined],
            }
          : undefined,
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.meta?.cursor,
    select: (data) => ({
      data: (data.pages as any).flatMap((item: any): Report[] => item.data),
      itemCount: (data.pages as any).flatMap(
        (item: any) => item.meta.itemCount
      ),
    }),
    refetchOnWindowFocus: false,
  });
};

export const useReportFeatured = (props: ReportALLProps) => {
  const { queryKey, isDescending, categoryIds } = props;

  const dispath = useAppDispatch();

  return useInfiniteQuery({
    queryKey: [queryKey, isDescending],
    queryFn: async () => {
      const params: reportFeaturedParams = {
        isDescending: true,
        categoryIds: categoryIds,
      };

      const result = await ReportServices.getReportFeatured(params);
      return result;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.meta?.cursor,
    select: (data) => (data.pages as any).flatMap((item: any) => item.data),
    refetchOnWindowFocus: false,
  });
};

export const useReportALLisLoggedIn = (props: ReportALLProps) => {
  const {
    queryKey,
    take,
    isDescending,
    onlyFavorite,
    userId,
    sortedField,
    isLoggedIn,
    types,
    lang,
    typeFavorite,
  } = props;

  return useInfiniteQuery({
    queryKey: [queryKey, isDescending, lang],
    queryFn: async ({ pageParam }) => {
      const params: paramsReport = {
        sortedField: sortedField,
        isDescending: isDescending,
        take: take,
        cursor: pageParam,
        onlyFavorite: onlyFavorite,
        userId: userId,
        types: types,
        typeFavorite: typeFavorite,
        lang: lang,
      };

      const result = await ReportServices.getAllReportHomePage(params);
      return result;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.meta?.cursor,
    select: (data) => ({
      data: (data.pages as any).flatMap((item: any) => item.data),
      itemCount: (data.pages as any).flatMap(
        (item: any) => item.meta.itemCount
      ),
    }),
    enabled: isLoggedIn,
    refetchOnWindowFocus: false,
  });
};

export const usePurchasedReports = (props: ReportALLProps) => {
  const {
    queryKey,
    take,
    isDescending,
    isRecentlyPurchase,
    initialData,
    lang,
  } = props;

  return useInfiniteQuery({
    queryKey: [queryKey, isDescending, isRecentlyPurchase],
    queryFn: async ({ pageParam }) => {
      const params: paramsPurchasedReports = {
        isDescending: isDescending,
        take: take,
        cursor: pageParam,
        isRecentlyPurchase: isRecentlyPurchase,
        lang: lang,
      };

      const result = await ReportServices.getPurchasedReports(params);
      return result;
    },
    initialData:
      isDescending && !isRecentlyPurchase
        ? { pages: [initialData], pageParams: [undefined] }
        : undefined,
    placeholderData: (res) => res,
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.meta?.cursor,
    select: (data) => ({
      data: (data.pages as any).flatMap((item: any) => item.data),
      itemCount: (data.pages as any).flatMap(
        (item: any) => item.meta.itemCount
      ),
    }),
    refetchOnWindowFocus: false,
    staleTime: 0,
  });
};

export const useAddFavoriteLatest = ({
  queryKey,
}: {
  queryKey: (string | number | boolean | null | undefined)[];
}) => {
  const t = useTranslations("messages");
  const queryClient = useQueryClient();
  const dipatch = useDispatch();
  return useMutation({
    mutationFn: async (reportId: number) => {
      const result = await ReportServices.postAddFavorite(
        reportId,
        TYPE_FAVORITE.FAVORITE
      );
      await queryClient?.cancelQueries(queryKey as any);
      const previousData = queryClient?.getQueryData(queryKey);
      return { data: result, previousData };
    },
    onSuccess: (response: any) => {
      const { reportId, checked } = response?.data;
      queryClient?.setQueryData(queryKey, (oldData: any) => {
        const newData = {
          ...oldData,
          pages: oldData?.pages.map((page: any) => ({
            ...page,
            data: page?.data.map((report: any) => {
              if (report.id === reportId) {
                return {
                  ...report,
                  isFavorite: checked,
                  totalFavorite: report.totalFavorite + 1,
                };
              }
              return report;
            }),
          })),
        };

        return newData;
      });
    },
    onError: (error: any, variables, context: any) => {
      dipatch(setErrorMess(t("common_something_went_wrong")));
      queryClient?.setQueryData(
        [queryKey],
        (oldData: any) => context?.previousData
      );
    },
  });
};

export const useAddFavoritePaymentHistory = ({
  queryKey,
}: {
  queryKey: (string | number | boolean | null | undefined)[];
}) => {
  const t = useTranslations("messages");
  const queryClient = useQueryClient();
  const dipatch = useDispatch();
  return useMutation({
    mutationFn: async (reportId: number) => {
      const result = await ReportServices.postAddFavorite(
        reportId,
        TYPE_FAVORITE.FAVORITE
      );
      await queryClient?.cancelQueries(queryKey as any);
      const previousData = queryClient?.getQueryData(queryKey);
      return { data: result, previousData };
    },
    onSuccess: (response: any) => {
      const { reportId, checked } = response?.data;
      queryClient?.setQueryData(queryKey, (oldData: any) => {
        const newData = {
          ...oldData,
          pages: oldData?.pages.map((page: any) => ({
            ...page,
            data: page?.data.map((payment: any) => {
              if (payment.report.id === reportId) {
                return {
                  ...payment,
                  report: {
                    ...payment.report,
                    isFavorite: checked,
                    totalFavorite: payment.report.totalFavorite + 1,
                  },
                };
              }
              return payment;
            }),
          })),
        };

        return newData;
      });
    },
    onError: (error: any, variables, context: any) => {
      dipatch(setErrorMess(t("common_something_went_wrong")));
      queryClient?.setQueryData(
        [queryKey],
        (oldData: any) => context?.previousData
      );
    },
  });
};

export const useAddFavoriteSingle = ({
  queryKey,
}: {
  queryKey: (string | number | boolean | null | undefined)[];
}) => {
  const t = useTranslations("messages");
  const queryClient = useQueryClient();
  const dipatch = useDispatch();
  return useMutation({
    mutationFn: async (reportId: number) => {
      const result = await ReportServices.postAddFavorite(
        reportId,
        TYPE_FAVORITE.FAVORITE
      );
      await queryClient?.cancelQueries(queryKey as any);
      const previousData = queryClient?.getQueryData(queryKey);
      return { data: result, previousData };
    },
    onSuccess: (response: any) => {
      const { reportId, checked } = response?.data;
      queryClient?.setQueryData(queryKey, (oldData: any) => {
        const newData = {
          ...oldData,
          isFavorite: checked,
        };

        return newData;
      });
    },
    onError: (error: any, variables, context: any) => {
      dipatch(setErrorMess(t("common_something_went_wrong")));
      queryClient?.setQueryData(
        [queryKey],
        (oldData: any) => context?.previousData
      );
    },
  });
};

export const useAddFavoriteOptions = ({
  queryKey,
}: {
  queryKey: (string | number | boolean | null | undefined)[];
}) => {
  const t = useTranslations("messages");
  const queryClient = useQueryClient();
  const dipatch = useDispatch();
  return useMutation({
    mutationFn: async (reportId: number) => {
      const result = await ReportServices.postAddFavorite(
        reportId,
        TYPE_FAVORITE.FAVORITE
      );
      await queryClient?.cancelQueries(queryKey as any);
      const previousData = queryClient?.getQueryData(queryKey);
      return { data: result, previousData };
    },
    onSuccess: (response: any) => {
      const { reportId, checked } = response?.data;
      queryClient?.setQueryData(queryKey, (oldData: any) => {
        const newData = oldData?.map((report: any) => {
          if (report.id === reportId) {
            return {
              ...report,
              isFavorite: checked,
              totalFavorite: report.totalFavorite + 1,
            };
          }
          return report;
        });

        return newData;
      });
    },
    onError: (error: any, variables, context: any) => {
      dipatch(setErrorMess(t("common_something_went_wrong")));
      queryClient?.setQueryData(
        [queryKey],
        (oldData: any) => context?.previousData
      );
    },
  });
};

export const useRelatedReports = (
  params: {
    relatedReportIds: number[];
  },
  options?: Omit<
    UseQueryOptions<Report[], unknown, Report[]>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: ["report-related", params],
    queryFn: async () => {
      const result = await ReportServices.getAllRelatedReport(params);
      return result;
    },
    ...options,
  });
};
